/* eslint-disable no-restricted-syntax */
/** @jsxImportSource @emotion/react */
import { faDownload } from "@fortawesome/pro-regular-svg-icons";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import * as XLSX from "xlsx";
import { Layer } from "../../../actions/Layers/constants";
import { format2Frontend } from "../../../actions/Layers/util";
import useConfig, { useTenantFormOptions } from "../../../actions/Tenants/config/configHook";
import { RootConfigType } from "../../../actions/Tenants/config/constantsTyped";
import { User } from "../../../actions/Users/actions";
import { RootState } from "../../../reducers";
import { toast } from "../../../utils/toast";
import { Button, ButtonProps } from "../../Helper/Button";
import Field, { FormOptions } from "./fields/Field";
import { formatDateForFilename } from "./fields/formatters";
import { useSwitchTabContext } from "./fields/useSwitchTab";

const MAX_EXPORT_ROWS = 200000;

const downloadExcelFile = (layers: Layer[], filteredFields: Field[], config: RootConfigType, form_options: FormOptions, active_tab: string | undefined, user: User) => {
    if (layers.length === 0) {
        return;
    }

    try {
        const worksheetData = layers.map((layer) => {
            const row: Record<string, string> = {};
            filteredFields.forEach((field) => {
                const valueObj = field.set_value({
                    config,
                    layer,
                    check: null,
                    fruit: null,
                    children: null,
                    form_options,
                    user
                })?.formatted_value();

                // Store the value in the row, ensuring it's a string or empty string
                row[field.label] = valueObj != null ? valueObj.toString() : "";
            });
            return row;
        });

        const header = filteredFields.map((field) => field.label);

        // Create a new workbook and add a worksheet
        const workbook = XLSX.utils.book_new();

        // Create worksheet with field names as headers
        const worksheet = XLSX.utils.json_to_sheet(worksheetData, { header });

        // Append the worksheet to the workbook
        const datetime = formatDateForFilename();
        XLSX.utils.book_append_sheet(workbook, worksheet, `export-${datetime}`);

        // Write the Excel file
        const fileName = active_tab ? `export-${active_tab}-${datetime}.xlsx` : `export-${datetime}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error generating Excel file:", error);
    }
};

export const DownloadExcelButton = (props: ButtonProps) => {
    const { active_tab, downloadFields, streamLayersForExport, layers } = useSwitchTabContext();
    const [downloading, setDownloading] = useState(false);
    const [showTruncationWarning, setShowTruncationWarning] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const config: RootConfigType = useConfig();
    const form_options = useTenantFormOptions();
    const user = useSelector<RootState, RootState["auth"]["user"]>((state) => state.auth.user);

    const exportLayers = useCallback(async () => {
        try {
            setDownloading(true);
            const allLayers: Layer[] = [];
            const stream = streamLayersForExport({ limit: MAX_EXPORT_ROWS, chunk_size: 2000 });

            for await (const chunk of stream) {
                allLayers.push(...chunk.results.map(format2Frontend));
            }

            if (allLayers.length === 0) {
                toast.warning("Results not found");
            } else {
                downloadExcelFile(allLayers, downloadFields, config, form_options, active_tab, user);
                if (totalCount > MAX_EXPORT_ROWS) {
                    toast.warning(`Exported first ${MAX_EXPORT_ROWS.toLocaleString()} of ${totalCount.toLocaleString()} matching records.`);
                }
            }
        } catch (error: any) {
            toast.error("An error occurred during the download. Please try again later.");
        } finally {
            setDownloading(false);
        }
    }, [streamLayersForExport, downloadFields, config, form_options, active_tab, user, totalCount]);

    const proceedWithExport = useCallback(async () => {
        setShowTruncationWarning(false);
        await exportLayers();
    }, [exportLayers]);

    const cancelExport = useCallback(() => {
        setShowTruncationWarning(false);
        setDownloading(false);
    }, []);

    const download = useCallback(() => {
        const count = layers?.count ?? 0;
        if (count === 0) {
            toast.warning("Results not found");
            return;
        }

        setTotalCount(count);
        setShowTruncationWarning(true);
    }, [layers?.count]);

    return (
        <>
            <Button color="light" onClick={download} disabled={downloading} icon={faDownload} {...props} />
            <Modal isOpen={showTruncationWarning} toggle={cancelExport}>
                <ModalHeader toggle={cancelExport}>Confirm Export</ModalHeader>
                <ModalBody>
                    <p>
                        Found <strong>{totalCount.toLocaleString()}</strong> matching rows.
                    </p>
                    {totalCount > MAX_EXPORT_ROWS && (
                        <p>
                            Only the first <strong>{MAX_EXPORT_ROWS.toLocaleString()}</strong> rows will be exported.
                        </p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={cancelExport}>Cancel</Button>
                    <Button color="primary" onClick={proceedWithExport}>
                        {totalCount > MAX_EXPORT_ROWS ? `Export First ${MAX_EXPORT_ROWS.toLocaleString()} Rows` : "Export"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};
